import { queryOptions, skipToken } from '@tanstack/lit-query';
import { api } from 'Api/client';
import { buildURL, fetchRequest } from 'Utils/api-fetch-request';

const KOL_VIDEOS_BASE_URL = '/api/videos/kol/';
const VIDEOS_BASE_URL = '/api/videos/';

interface FetchKolVideosParams {
    kolUuid: string;
    ordering: string;
    limit: number;
    offset: number;
}

export interface KolVideosResponse {
    count: number;
    results: Video[];
}

export interface Video {
    slug: string;
    thumbnail_small: string;
    thumbnail_large: string;
    duration: number;
    title: string;
    contributors: Author[];
    author: Author;
    created: string;
    views: number;
    brightcove_id: string;
    description: string;
    channel: Channel | null;
}

export interface Channel {
    author: Author;
    company: string;
    description: string;
    logo_large: string;
    other_admins: Author[];
    safety_information: string;
    slug: string;
    title: string;
}

export interface Author {
    avatar: string;
    company: string | null;
    date_joined: Date;
    first_name: string;
    last_name: string;
    position: string | null;
    site: { slug: string; text: string; display: string; root: boolean };
    title: string | null;
    uuid: string;
}

export interface VideoSlidesResponse {
    video_id: number;
    video_slug: string;
    slide_count: number;
    url_expires_at: string;
    slides: VideoSlide[];
}

export interface VideoSlide {
    id: number;
    slide_number: number;
    title: string;
    /** Slide image contents */
    image_url: string;
    /** Nullable; downscaled slide image contents or thumbnail */
    thumbnail_url?: string | null;
    /** First appearance of slide in video (in seconds) */
    start_time: number;
    /** Last appearance of slide in video (in seconds) */
    end_time: number;
}

export interface ShareVideoParams {
    to: string[];
    message: string;
}

export async function fetchKolVideos({
    kolUuid,
    ordering,
    limit,
    offset,
}: FetchKolVideosParams): Promise<KolVideosResponse> {
    const url = buildURL(`${KOL_VIDEOS_BASE_URL}${kolUuid}/`, { ordering, limit, offset });
    return fetchRequest(url);
}

export async function thumbsupVideo(slug: string): Promise<void> {
    await fetchRequest(`${VIDEOS_BASE_URL}${slug}/thumbup/`, { method: 'POST' });
}

export async function removeThumbsupVideo(slug: string): Promise<void> {
    await fetchRequest(`${VIDEOS_BASE_URL}${slug}/thumbup/`, { method: 'DELETE' });
}

export async function bookmarkVideo(slug: string): Promise<void> {
    await fetchRequest(`${VIDEOS_BASE_URL}${slug}/bookmark/`, { method: 'POST' });
}

export async function removeBookmarkVideo(slug: string): Promise<void> {
    await fetchRequest(`${VIDEOS_BASE_URL}${slug}/bookmark/`, { method: 'DELETE' });
}

export async function trackLearnMoreClick(slug: string): Promise<void> {
    await fetchRequest(`/api/videos/${slug}/learn-more/`, { method: 'POST' });
}

export function fetchVideoSlides(slug: string, signal?: AbortSignal) {
    return api.get(`videos/${encodeURIComponent(slug)}/slides/`, { signal }).json<VideoSlidesResponse>();
}

export function slidesQueryOptions(slug: string | undefined) {
    return queryOptions({
        queryKey: ['slides', slug] as const,
        queryFn: slug ? ({ signal }) => fetchVideoSlides(slug, signal) : skipToken,
    });
}

/**
 * Mint a Brightcove PAS token for authorized (edge-auth) playback.
 *
 * Returns null when no token is available — either PAS is disabled (404) or the mint
 * failed (geo/throttle/sign). Callers currently fall back to the policy-key path.
 *
 * TRANSITION-ONLY fallback: once authorized delivery is enforced the policy-key path is
 * gone, so a null here must become a surfaced playback error rather than a silent fallback.
 */
export async function fetchPlaybackToken(slug: string): Promise<string | null> {
    try {
        const data = await fetchRequest(`${VIDEOS_BASE_URL}${slug}/playback-token/`);
        return data?.token ?? null;
    } catch {
        return null;
    }
}

export async function shareVideo(slug: string, { to, message }: ShareVideoParams): Promise<void> {
    await fetchRequest(`${VIDEOS_BASE_URL}${slug}/share/`, {
        method: 'POST',
        body: JSON.stringify({ to, message }),
    });
}
